import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchRecipes, type RecipeView } from '../api/missions';
import { ca } from '../i18n/ca';
import {
  SKILL_STOPS,
  clampSkill,
  formatSkill,
  nearestStop,
  parseSkill,
  skillRange,
  stopOf,
} from './enemySkill';
import { HowItWorks } from './HelpFold';
import { QuickStart } from './NewMission';
import {
  STEP,
  STEP2_KEY,
  readDraft,
  readStored,
  summariesOf,
  writeDraft,
  type MissionDraftStep2,
  type PartialDraft,
} from './newMissionDraft';
import { Shell } from './Shell';

/** The type's own first, then the three the generator knows, fewest first. */
const STRENGTHS = ['', 'light', 'medium', 'heavy'] as const;
const QUALITIES = ['vague', 'reasonable', 'good'];
/** What the field allowed while nothing said how many a type can make. */
const MARKS_UNKNOWN = 6;

/**
 * The most strongpoints a night of this type can carry: the places the enemy
 * holds on the main line, and one more for every secondary that has a place of
 * its own — the chosen ones, or every one offered while the seed decides. Null
 * while the type is not known, or when the platform is older than the field.
 */
function strongpointsOf(
  recipe: RecipeView | undefined,
  type: MissionDraftStep2 | null,
): number | null {
  if (!recipe || recipe.strongpoints == null) return null;
  const offered = (recipe.secondaries ?? []).filter((s) => s.strongpoint);
  const chosen = type?.secondaryObjectives ?? [];
  const taken =
    type?.secondaryBy === 'chance' ? offered : offered.filter((s) => chosen.includes(s.id));
  return recipe.strongpoints + taken.length;
}

/**
 * The strength the generator uses when nobody chooses one: the type's own,
 * moved one step for a party under or over the size it is written for
 * (`MissionGenerator.scaled`; the moved values are the platform's, never
 * worked out here). Null while the type is not known.
 */
function typeStrength(
  recipe: RecipeView | undefined,
  players: number | undefined,
): { strength: string; moved: 'under' | 'over' | null } | null {
  if (!recipe?.enemyStrength) return null;
  const enemy = recipe.enemy;
  if (!enemy || players == null) return { strength: recipe.enemyStrength, moved: null };
  if (players < recipe.minPlayers) {
    return {
      strength: enemy.strengthUnder,
      moved: enemy.strengthUnder !== enemy.strength ? 'under' : null,
    };
  }
  if (players > recipe.maxPlayers) {
    return {
      strength: enemy.strengthOver,
      moved: enemy.strengthOver !== enemy.strength ? 'over' : null,
    };
  }
  return { strength: enemy.strength, moved: null };
}

/**
 * The quick start, step 5, «Dificultat»: how many enemies, how good they are,
 * and what is known about them before anybody sets out.
 *
 * How many and how good were one answer until 2026-10-12: a strength, and a
 * skill that silently followed it unless a number was typed (0,20 · 0,30 ·
 * 0,40). They are two cards now and neither moves the other: the step always
 * writes the skill it shows, so the generator never derives one from the
 * strength behind the creator's back. The skill is still the one number Eden's
 * slider sets — the group's own missions stand their enemy at 0,30 — offered
 * as five named stops with the number under each, and as the number itself.
 *
 * What a strength means in groups and men is each type's script's to decide
 * and the platform cannot say it before generating, so the cards say what is
 * true of every type — fewer or more than the type would bring, and how many
 * groups the runtime keeps awake at once — and no figure of men.
 *
 * The intel is drawn from the order of battle after it is placed, so it points
 * at real trouble, and then it is spoiled on purpose: marks move off the truth
 * by the quality chosen here, zones are drawn wider than they are, and the
 * false marks point at nobody at all. That last one is a control and not a
 * mistake — intel that is never wrong is the enemy's own map, and a group that
 * learns to trust it stops scouting.
 */
export function NewMissionIntel() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<PartialDraft>(() => readDraft());
  const [strength, setStrength] = useState<string>(draft.enemyStrength ?? '');
  /** Null until somebody sets one: the platform's own default, shown as chosen. */
  const [skill, setSkill] = useState<number | null>(draft.enemySkill ?? null);
  /** What the exact field holds while it is typed in; null follows the skill. */
  const [exact, setExact] = useState<string | null>(null);
  const [findBodies, setFindBodies] = useState<boolean>(draft.enemiesFindBodies ?? true);
  const [area, setArea] = useState<boolean>(draft.intelArea ?? true);
  const [strongpoints, setStrongpoints] = useState<boolean>(draft.intelStrongpoints ?? true);
  const [quality, setQuality] = useState<string>(draft.intelQuality ?? 'reasonable');
  const [marks, setMarks] = useState<number>(draft.intelMarks ?? 2);
  const [wrong, setWrong] = useState<number>(draft.intelFalse ?? 1);
  // How many strongpoints the mission will really have. The field took up to six
  // whatever the type, and the night drew the one it had (session 3gasy): what
  // can be asked for is what will be generated, and the number is written down.
  const [type] = useState(() => readStored<MissionDraftStep2>(STEP2_KEY));
  const [recipe, setRecipe] = useState<RecipeView | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    fetchRecipes()
      .then((all) => {
        if (alive) setRecipe(all.find((r) => r.id === type?.recipeId));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [type]);
  const most = strongpointsOf(recipe, type);
  const cap = most ?? MARKS_UNKNOWN;
  const shownMarks = Math.min(marks, cap);

  const t = ca.missionUi.intel;
  const range = skillRange(recipe?.enemy);
  const shownSkill = clampSkill(skill ?? range.default, range);
  const stops = SKILL_STOPS.filter((s) => s.value >= range.min && s.value <= range.max);
  const fromType = typeStrength(recipe, type?.players);

  if (!draft.serverId || !draft.worldName) {
    return (
      <Shell title={ca.newMission}>
        <p className="muted">{ca.missionUi.details.pickFirst}</p>
      </Shell>
    );
  }

  function next() {
    setDraft(
      writeDraft({
        enemyStrength: strength || null,
        // Always the number shown: left empty, the generator would take it from the strength.
        enemySkill: shownSkill,
        enemiesFindBodies: findBodies,
        intelArea: area,
        intelStrongpoints: strongpoints,
        intelQuality: quality,
        intelMarks: strongpoints ? shownMarks : 0,
        intelFalse: wrong,
      }),
    );
    navigate('/new/forces');
  }

  /** What a strength card says under its name: against the type's own, and the runtime's budget. */
  function countLines(s: string): string[] {
    const lines: string[] = [];
    const groups = recipe?.enemy?.activeGroups;
    if (s === '') {
      if (!fromType) return [t.countFromTypeUnknown];
      const name = t.countNames[fromType.strength] ?? t.countFromTypeUnknown;
      const players = type?.players ?? 0;
      lines.push(
        fromType.moved === 'under' && recipe
          ? t.countMovedUnder(players, recipe.minPlayers, name)
          : fromType.moved === 'over' && recipe
            ? t.countMovedOver(players, recipe.maxPlayers, name)
            : t.countFromType(name),
      );
      if (groups?.[fromType.strength] != null) lines.push(t.countGroups(groups[fromType.strength]));
      return lines;
    }
    if (fromType) {
      const order = STRENGTHS.indexOf(s as (typeof STRENGTHS)[number]);
      const own = STRENGTHS.indexOf(fromType.strength as (typeof STRENGTHS)[number]);
      if (own > 0) lines.push(order < own ? t.countLess : order > own ? t.countMore : t.countSame);
    }
    if (groups?.[s] != null) lines.push(t.countGroups(groups[s]));
    return lines;
  }

  function pickStop(value: number) {
    setSkill(value);
    setExact(null);
  }

  function typeExact(text: string) {
    setExact(text);
    const n = parseSkill(text);
    if (n != null) setSkill(clampSkill(n, range));
  }

  const countCard = (
    <section className="card form" aria-label={t.countTitle}>
      <h2>{t.countTitle}</h2>
      <p className="muted small">{t.countHelp}</p>
      <div className="choice-cards" role="radiogroup" aria-label={t.countTitle}>
        {STRENGTHS.map((s) => {
          const id = `strength-${s || 'type'}`;
          return (
            <label key={s} className={`choice-card ${strength === s ? 'selected' : ''}`}>
              <strong id={`${id}-name`}>{t.countNames[s]}</strong>
              <span id={`${id}-what`} className="muted small">
                {countLines(s).map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </span>
              {/* Last, not first: a label whose first child is a radio is laid out as a row. */}
              <input
                type="radio"
                name="enemyStrength"
                value={s}
                checked={strength === s}
                aria-labelledby={`${id}-name`}
                aria-describedby={`${id}-what`}
                onChange={() => setStrength(s)}
              />
            </label>
          );
        })}
      </div>
      <HowItWorks>
        {t.countHow.map((p) => (
          <p key={p} className="muted small">
            {p}
          </p>
        ))}
      </HowItWorks>
    </section>
  );

  const level = nearestStop(shownSkill);
  const skillCard = (
    <section className="card form" aria-label={t.skillTitle}>
      <h2>{t.skillTitle}</h2>
      <p className="muted small">{t.skillHelp}</p>
      <div className="skill-scale" role="radiogroup" aria-label={t.skillTitle}>
        {stops.map((s) => {
          const own = Math.abs(s.value - range.default) < 0.005;
          const on = stopOf(shownSkill) === s.id;
          return (
            <label key={s.id} className={`skill-stop ${on ? 'selected' : ''}`}>
              <strong>{t.skillStops[s.id]}</strong>
              <span className="num">{formatSkill(s.value)}</span>
              {own && <span className="chip">{t.skillOwn}</span>}
              <input
                type="radio"
                name="enemySkillStop"
                value={s.value}
                checked={on}
                aria-label={`${t.skillStops[s.id]} ${formatSkill(s.value)}${own ? ` (${t.skillOwn})` : ''}`}
                onChange={() => pickStop(s.value)}
              />
            </label>
          );
        })}
      </div>
      <p className="skill-feel" role="status">
        <strong>
          {t.skillStops[level]} · {formatSkill(shownSkill)}
        </strong>{' '}
        {t.skillFeel[level]}
      </p>
      <label className="skill-exact">
        {t.skillExact}
        <input
          name="enemySkill"
          type="number"
          inputMode="decimal"
          min={range.min}
          max={range.max}
          step={0.05}
          value={exact ?? String(shownSkill)}
          aria-describedby="skill-range"
          onChange={(e) => typeExact(e.target.value)}
          onBlur={() => setExact(null)}
        />
        <span id="skill-range" className="muted small">
          {t.skillRange(formatSkill(range.min), formatSkill(range.max))}
        </span>
      </label>
      <HowItWorks>
        {t.skillHow(formatSkill(range.min), formatSkill(range.max)).map((p) => (
          <p key={p} className="muted small">
            {p}
          </p>
        ))}
      </HowItWorks>

      <label className="switch">
        <input
          type="checkbox"
          name="enemiesFindBodies"
          checked={findBodies}
          onChange={(e) => setFindBodies(e.target.checked)}
        />
        {ca.missionUi.details.enemiesFindBodies}
      </label>
      <p className="muted small">{t.findBodiesHelp}</p>
      <HowItWorks>
        <p className="muted small">{ca.missionUi.details.enemiesFindBodiesHint}</p>
      </HowItWorks>
    </section>
  );

  // What the players are told sits beside how many and how good, rather than
  // under them: one screen, no scrolling on a wide one (QUICK_START_REVIEW.md §2.1).
  const intelCard = (
    <section className="card form" aria-label={t.title}>
      <h2>{t.title}</h2>

      <label className="switch">
        <input
          type="checkbox"
          name="intelArea"
          checked={area}
          onChange={(e) => setArea(e.target.checked)}
        />
        {t.area}
      </label>
      <p className="muted small">{t.areaHelp}</p>

      <label className="switch">
        <input
          type="checkbox"
          name="intelStrongpoints"
          checked={strongpoints && cap > 0}
          disabled={cap === 0}
          onChange={(e) => setStrongpoints(e.target.checked)}
        />
        {t.strongpoints}
      </label>
      <p className="muted small">{most !== null ? t.strongpointsMost(most) : t.strongpointsHelp}</p>

      <label>
        {t.marks}
        <input
          name="intelMarks"
          type="number"
          min={0}
          max={cap}
          value={shownMarks}
          disabled={!strongpoints || cap === 0}
          onChange={(e) => setMarks(Math.max(0, Math.min(cap, Number(e.target.value) || 0)))}
        />
      </label>

      <label>
        {t.quality}
        <select
          name="intelQuality"
          value={quality}
          onChange={(e) => setQuality(e.target.value)}
          disabled={!area && !strongpoints}
        >
          {QUALITIES.map((q) => (
            <option key={q} value={q}>
              {t.qualityNames[q] ?? q}
            </option>
          ))}
        </select>
      </label>
      <p className="muted small">{t.qualityHelp}</p>

      <label>
        {t.wrong}
        <input
          name="intelFalse"
          type="number"
          min={0}
          max={3}
          value={wrong}
          onChange={(e) => setWrong(Math.max(0, Math.min(3, Number(e.target.value) || 0)))}
        />
      </label>
      <p className="muted small">{t.wrongHelp}</p>

      <HowItWorks>
        <p className="muted small">{t.blurb}</p>
        <p className="muted small">{t.qualityHint}</p>
        <p className="muted small">{t.wrongHint}</p>
      </HowItWorks>
    </section>
  );

  return (
    <Shell
      title={ca.newMission}
      width="wide"
      actions={
        <>
          <button type="button" onClick={() => navigate('/new/enemy')}>
            {ca.back}
          </button>
          <button type="button" className="primary" onClick={next}>
            {ca.next}
          </button>
        </>
      }
    >
      <QuickStart step={STEP.intel} summaries={summariesOf(draft, type)} aside={intelCard}>
        {countCard}
        {skillCard}
      </QuickStart>
    </Shell>
  );
}
